import type RTKClient from '@cloudflare/realtimekit';
import type { AudioMiddleware } from '@cloudflare/realtimekit';
import rnnoiseWorkletUrl from '@sapphi-red/web-noise-suppressor/rnnoiseWorklet.js?url';
import rnnoiseWasmUrl from '@sapphi-red/web-noise-suppressor/rnnoise.wasm?url';
import rnnoiseSimdWasmUrl from '@sapphi-red/web-noise-suppressor/rnnoise_simd.wasm?url';
import { loadRnnoise, RnnoiseWorkletNode } from '@sapphi-red/web-noise-suppressor';

// Tier-1 client-side RNNoise (WASM) noise suppression as a live SDK audio
// middleware. The SDK calls our middleware with its OWN AudioContext, chains
// the returned node into the mic graph, and live-swaps the produced track via
// RTCRtpSender.replaceTrack — no rejoin. Removal matches by FUNCTION REFERENCE,
// so the exact same `rnnoiseMiddleware` reference must be reused for add/remove.
//
// Double-processing note: in Voice the browser's built-in `noiseSuppression`
// (getUserMedia constraint) stays ON and RNNoise stacks after capture as the
// stronger ML layer. This is intentional — disabling the built-in NS would
// require a capture re-init (rejoin), which we avoid for a live toggle. NC is
// disallowed for Music (no voice processing) by the caller.

// Cache the wasm binary once (loadRnnoise picks SIMD vs non-SIMD via feature
// detect). Reused for every node construction, across AudioContext rebuilds.
let wasmBinaryPromise: Promise<ArrayBuffer> | undefined;
function getRnnoiseWasm(): Promise<ArrayBuffer> {
  if (!wasmBinaryPromise) {
    wasmBinaryPromise = loadRnnoise({ url: rnnoiseWasmUrl, simdUrl: rnnoiseSimdWasmUrl });
  }
  return wasmBinaryPromise;
}

// The SDK creates a fresh AudioContext on every (re)transform, so the worklet
// module must be registered once per context. Track which contexts already have
// it to avoid 'A processor was already registered' errors.
const registered = new WeakSet<BaseAudioContext>();

/**
 * Stable middleware reference. MUST be the same identity for add + remove —
 * removeAudioMiddleware matches via Array.indexOf. The SDK passes its own
 * AudioContext; we register the worklet on THAT ctx then return one node.
 */
const rnnoiseMiddleware: AudioMiddleware = async (audioContext) => {
  const wasmBinary = await getRnnoiseWasm();
  if (!registered.has(audioContext)) {
    await audioContext.audioWorklet.addModule(rnnoiseWorkletUrl);
    registered.add(audioContext);
  }
  return new RnnoiseWorkletNode(audioContext, {
    maxChannels: 1, // mono voice
    wasmBinary,
  });
};

/**
 * Enable RNNoise on the live mic track (no rejoin). Returns whether it actually
 * applied. The SDK does NOT throw when middleware is unsupported (WebKit/Safari)
 * or when the worklet/wasm fails to load — it returns { success:false } and
 * silently removes the half-applied middleware. We surface that so the UI can
 * avoid falsely showing "On". A thrown error is also treated as failure.
 */
export async function enableNC(meeting: RTKClient): Promise<boolean> {
  try {
    const status = await meeting.self.addAudioMiddleware(rnnoiseMiddleware);
    if (!status.success) {
      console.warn('Noise cancellation unavailable:', status.message);
    }
    return status.success;
  } catch (err) {
    console.warn('Noise cancellation failed to enable:', err);
    return false;
  }
}

/**
 * Disable RNNoise on the live mic track (no rejoin). Passing the same stable
 * reference is required for removeAudioMiddleware to match. Best-effort —
 * removing a not-applied middleware is a harmless no-op.
 */
export async function disableNC(meeting: RTKClient): Promise<void> {
  try {
    await meeting.self.removeAudioMiddleware(rnnoiseMiddleware);
  } catch (err) {
    console.warn('Noise cancellation failed to disable:', err);
  }
}

/**
 * Apply the desired NC state live. Returns true if the resulting state matches
 * `enabled` (enable applied, or disable completed). Mirrors setAudioBitrate's
 * fire-and-forget call shape, but reports success so callers can keep UI honest.
 */
export async function setNoiseCancellation(meeting: RTKClient, enabled: boolean): Promise<boolean> {
  if (enabled) {
    return enableNC(meeting);
  }
  await disableNC(meeting);
  return true;
}
